import { useMemo } from "react"
import { useSelector } from "react-redux"

import type { Device } from "types/device.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useDevicesQuery } from "features/api"
import { getSpanDevices } from "helpers/utils/spans"

interface UseSpanDevicesProps {
  spanId: number | undefined
}

interface UseSpanDevicesReturn {
  devices: Device[]
  isError: boolean
  isFetching: boolean
  isLoading: boolean
}

/**
 * The devices installed on a span. Devices are only listed per organization, so
 * this reads the org's list and narrows it. The query is the one the rest of the
 * board already holds, so it usually resolves from cache.
 */
const useSpanDevices = ({ spanId }: UseSpanDevicesProps): UseSpanDevicesReturn => {
  const orgId = useSelector(selectCurrentOrgId)

  const { data, isError, isFetching, isLoading } = useDevicesQuery(
    { params: { org_id: orgId as number } },
    { skip: !orgId || !spanId },
  )

  const devices = useMemo(() => getSpanDevices(data, spanId), [data, spanId])

  return { devices, isError, isFetching, isLoading }
}

export default useSpanDevices
